<template>
    <article>
        <p>
            Adopting a microservice architecture can unleash development agility and productivity, but it can also
            significantly increase the complexity of a system. From the ops perspective, a single cluster can run dozens or
            hundreds of services that compete for computing resources and communicate with each other over a network across
            several availability zones. Even the slightest instability in such a system can start a domino effect.
            For example, a network latency increase between two random nodes in a cluster can affect dozens of microservices at once.
        </p>

        <p>
            We built Coroot to simplify troubleshooting even in complex systems.
            One of the key differences between Coroot and other tools out there is its ability to discover any
            distributed system topology automatically.
            This makes it possible to find the issues that definitely affect a particular application without any
            configuration other than defining SLOs (Service Level Objectives).
            In other words, you only need to define how you expect the service to work.
            For example, the SLOs of a web service can look something like this:
        </p>

        <ul>
            <li><b>Availability</b>: 99% of requests should not fail</li>
            <li><b>Latency</b>: 99% of requests should be served in less than 100ms</li>
        </ul>

        <p>
            Once you define the SLOs of a service, Coroot starts to track their compliance.
        </p>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/just-define-your-slo/slo-report.png" />
        </v-card>

        <h2>Incident intelligence automation</h2>

        <p>
            Whenever a service does not meet the specified SLOs,
            Coroot initiates an automated investigation to find the issues affecting that service.
            Each of the following inspections checks the correlation between the affected app's SLIs (Service Level Indicators)
            and metrics describing a subsystem:
        </p>

        <ul>
            <li>
                <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'inspections', item: 'cpu'}}">CPU</router-link>:
                identifies when an app is experiencing a lack of CPU time due to throttling or high CPU usage on the related nodes.
            </li>
            <li>
                <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'inspections', item: 'memory'}}">Memory</router-link>:
                detects situations where an app's containers have been terminated by the OOM (Out Of Memory) killer.
            </li>
            <li>
                <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'inspections', item: 'storage'}}">Storage</router-link>:
                identifies when the performance of a storage volume is affecting the application.
            </li>
            <li>
                <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'inspections', item: 'instances'}}">Instances</router-link>:
                detects when one or more instances of an app are unavailable or have been restarted.
            </li>
            <li>
                <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'inspections', item: 'logs'}}">Logs</router-link>:
                audits log-based metrics and highlights the errors correlating with the SLIs of an app.
            </li>
            <li>
                <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'inspections', item: 'network'}}">Network</router-link>:
                detects network issues, such as increased latency or packet loss.
            </li>
            <li>
                <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'inspections', item: 'postgres'}}">Postgres</router-link>:
                identifies issues related to Postgres databases.
            </li>
        </ul>

        <p>
            As a result, Coroot detects issues related to a particular application and its upstream services and databases.
        </p>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/just-define-your-slo/outages.png" />
        </v-card>

        <p>
            The main advantage of such automation is that it can verify hundreds of hypotheses regarding
            the root cause of an outage in just one second.
            So, most likely, by the time you are notified, the root cause of the incident will already have been detected.
            With the  "Outage context" feature, you can drill down to a particular outage to view its details and possible fixes
            or cross-check with verbose data.
        </p>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/just-define-your-slo/outage-details.png" />
        </v-card>

        <h2>Alerting</h2>

        <p>
            The purpose of any alerting system is to notify a team of every significant service outage.
            However, it can be tricky to construct alert rules to only detect significant events.
        </p>

        <p>
            No SLO can require 100% availability, because it is impossible to achieve.
            So, for instance, if we aim to enforce 99% of availability over 30 days,
            the service can fail 1% of requests without making its users unhappy.
            This 1% is called <var>Error Budget</var>.
            In other words, we can say that the main goal is not to overspend the error budget.
            Therefore, an outage is significant if the budget is being consumed too quickly.
        </p>

        <p>
            Coroot's SLO-based alerting uses the predefined multi-window burn rate thresholds:
        </p>

        <v-card class="pa-4 mb-5">
            <BurnRateThresholds />
        </v-card>

        <p>
            An alert will be fired if the error budget burn rate is greater than the threshold in both long and short windows.
            Checking in the short window makes sure that the error budget is still being consumed.
        </p>

        <h2>Conclusion</h2>

        <p>
            Using SLOs is a great way to define user expectations of service reliability for engineering teams.
            Coroot uses SLOs not only for alerting but also for automating incident intelligence.
            Accompanying each alert with the incident root cause allows our customers to reduce MTTD (Mean Time To Detect) to almost zero.
        </p>

        <v-card max-width="600" class="pa-4 mb-4">
            <v-img eager src="/static/img/docs/slack_msg.png" alt="" />
        </v-card>

        <ul>
            <li>
                <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'getting-started', item: 'installation'}}">Get started with Coroot</router-link>
            </li>
            <li>
                <a href="https://community-demo.coroot.com" target="_blank">Coroot live demo</a>
            </li>
            <li>
                The detailed explanation of SLO-based alerting you can find in <a href="https://sre.google/workbook/alerting-on-slos/" target="_blank">The SRE Workbook</a>.
            </li>
        </ul>
    </article>
</template>

<script>
import BurnRateThresholds from "@/components/BurnRateThresholds.vue";

export default {
    components: {BurnRateThresholds},
}
</script>

<style scoped>
</style>